import { UserRole } from './invite-to-organization.dto';

export class WhatsAppLinkDto {
  title: string;

  description: string;

  url: string;
}

export type WhatsAppLink = {
  title: string;
  description: string;
  url: string;
};

export type OrganizationDto = {
  id: string;
  name: string;
  // The CALLER's own role in THIS organization, resolved server-side from
  // their OrganizationMember row. Only caller-scoped endpoints populate it —
  // `GET my-organizations` and `GET get-current-organization`.
  //
  // Absent/null means "unknown" (in practice: an elevated/platform admin, who
  // has no membership row), and consumers fall back to the global `role`
  // custom claim. See `features/auth/roles.ts`.
  //
  // Why it exists: the Firebase `role` claim is ONE value per user, so it
  // cannot describe someone who is ADMIN of org A and COACH of org B.
  myRole?: UserRole | null;
  // MYS-132 — when the CALLER completed (or skipped) the coach onboarding
  // tutorial in this org; null/absent = never. Caller-scoped like `myRole`
  // (populated only by `GET my-organizations` / `GET get-current-organization`).
  // The coach tour auto-launches only when NO org in the caller's list carries
  // a timestamp — see features/walkthrough/components/CoachOnboardingTour.tsx.
  myCoachTourCompletedAt?: string | null;
  members: OrganizationMemberDto[];
  kycCompleted: boolean;
  refundDeadline: number;
  lastMinuteDiscountPercentage: number;
  lastMinuteDiscountDuration: number;
  whatsappLinks: WhatsAppLink[];
  baseUrl: string;
  creditConversionRate: number;
  campaignFrequencyCapPerWeek: number;
  stripeAccountId: string;
  gameBookingCodeEnabled: boolean;
  accessCodeRevealLeadMinutes: number;
  accessCodeGraceMinutes: number;
  // MYS-87: minutes before start to push the access code; null = push off.
  accessCodeNotifyLeadMinutes: number | null;
  // How long an admin-initiated booking checkout session stays open before
  // Stripe expires it and the slot is auto-released. Default 30 minutes.
  bookingPaymentDeadlineMinutes: number;
  // CGU version (org-level default). NULL/undefined when never edited; defaults to 1
  // server-side. cguText is fetched on-demand via the dedicated GET /cgu endpoint
  // and is NOT included on the org DTO (keep payloads light).
  cguVersion?: number;
  // Optional per-tenant overrides for the cancellation modal copy. NULL = use
  // the translated default. Set/cleared via the dashboard cancellation form.
  cancellationMessageWithinDeadline?: string | null;
  cancellationMessageOutsideDeadline?: string | null;
  // Per-tenant gate for the "Pay for others" flow. Optional in the type so
  // older API responses that predate this field (or partial DTOs) don't
  // explode at the type level — treat missing as false in the UI.
  payForOthersEnabled?: boolean;
  // Max DISTINCT other players one payer may cover per rolling 24h on the
  // "Pay for others" flow. Optional for the same reason as payForOthersEnabled
  // — treat missing as the platform default (5) in the UI.
  payForOthersDailyCap?: number;
  // Guest (account-less) players on the pay-for-others flow; optional for the
  // same reason as payForOthersEnabled.
  guestPlayersEnabled?: boolean;
  guestsPerGameMax?: number;
  // Held seats: covered players must accept a seat somebody paid for them.
  payForOthersConsentRequired?: boolean;
  // Per-tenant gate for the coaching "Pay later" option. Optional for the same
  // reason as payForOthersEnabled — treat missing as false in the UI.
  coachingPayLaterEnabled?: boolean;
  // Per-tenant gate for the court-booking "Pay later" option. Optional for the
  // same reason as payForOthersEnabled — treat missing as false in the UI.
  courtPayLaterEnabled?: boolean;
  // Explicit recipient list for admin-notification emails (new booking,
  // cancellation, failed payment). Empty/missing = channel off. Always
  // normalized server-side (trimmed, lowercased, deduped).
  adminNotificationEmails?: string[];
  // Which chat backend the tenant's mobile clients read (Phase 6 cutover).
  // Flipped only by elevated/platform admins. Optional/missing → 'stream'.
  chatBackend?: ChatBackend;
  // True when the org has a provisioned VenueAgent. Gates the per-game
  // recording toggle in the admin booking modal. Optional/missing → false.
  recordingAvailable?: boolean;
  // Court-recording retention (per org, default 3 days, ceiling 90). EFFECTIVE
  // days a completed recording's video is kept — the org's override or the
  // platform default, clamped server-side. Show as-is; never re-derive.
  // Optional only for API responses that predate the field.
  recordingRetentionDays?: number;
  // True when the org has no override and is on the platform default.
  recordingRetentionIsDefault?: boolean;
  // The platform ceiling — the largest value the superadmin editor may offer.
  recordingRetentionMaxDays?: number;
  signupDocumentUploadEnabled?: boolean;
  // Whether the document gates signup completion. Optional/missing → true
  // (historical behavior: enabling uploads made them mandatory).
  signupDocumentUploadRequired?: boolean;
  signupDocumentUploadTitle?: string | null;
  signupDocumentUploadSubtitle?: string | null;
  documentVerificationEnabled?: boolean;
  documentVerificationMembershipTypeId?: string | null;
  documentVerificationCustomPrompt?: string | null;
};

export class UpdateOrganizationAccessCodeWindowDto {
  accessCodeRevealLeadMinutes: number;
  accessCodeGraceMinutes: number;
}

export class UpdateOrganizationBookingPaymentDeadlineDto {
  bookingPaymentDeadlineMinutes: number;
}

export class UpdateOrganizationGameBookingCodeEnabledDto {
  gameBookingCodeEnabled: boolean;
}

export class UpdateOrganizationCoachingPayLaterEnabledDto {
  coachingPayLaterEnabled: boolean;
}

export class UpdateOrganizationCourtPayLaterEnabledDto {
  courtPayLaterEnabled: boolean;
}

// Mirrors the API limits — the editor enforces them client-side for a decent
// error message, but the API re-validates and is the actual gate.
export const MAX_ADMIN_NOTIFICATION_EMAILS = 20;
export const MAX_ADMIN_NOTIFICATION_EMAIL_LENGTH = 254;

// Sending [] clears the list, which turns the email channel off. There is no
// separate enabled flag.
export class UpdateOrganizationAdminNotificationEmailsDto {
  adminNotificationEmails: string[];
}

export type OrganizationMemberDto = {
  id: string;
  email: string;
  createdAt: string;
  role: UserRole;
};

export type OrganizationInvitationDto = {
  id: string;
  email: string;
  // null when sent by an elevated/platform admin who is not a member of the org.
  sender: OrganizationMemberDto | null;
  createdAt: string;
  updatedAt: string;
  isAccepted: boolean;
  // Derived from createdAt + the API's invitation TTL — there is no expiry
  // column. An expired invitation cannot be accepted and does not heal itself;
  // the remedy is for an admin to revoke it and invite again.
  expiresAt: string;
  isExpired: boolean;
};

export class UpdateOrganizationRefundDeadlineDto {
  refundDeadline: number;
}

export class UpdateOrganizationDiscountDto {
  lastMinuteDiscountPercentage: number;

  lastMinuteDiscountDuration: number;
}

export class UpdateOrganizationWhatsAppLinksDto {
  whatsappLinks: WhatsAppLinkDto[];
}

export class UpdateOrganizationCreditConversionRateDto {
  creditConversionRate: number;
}

export class UpdateOrganizationCampaignFrequencyCapDto {
  campaignFrequencyCapPerWeek: number;
}

export class UpdateOrganizationEloSettingsDto {
  eloKFactor?: number;

  eloScalingFactor?: number;

  eloMarginOfVictoryEnabled?: boolean;

  eloMaxLevelAdjustment?: number;
}

export type OrganizationEloSettingsDto = {
  eloKFactor: number;
  eloScalingFactor: number;
  eloMarginOfVictoryEnabled: boolean;
  eloMaxLevelAdjustment: number;
};

export type WhatsAppConfigResponseDto = {
  whatsappEnabled: boolean;
  whatsappPhoneNumberId?: string;
  whatsappBusinessAccountId?: string;
  hasAccessToken: boolean;
  accessTokenLastChars?: string;
  whatsappReplacementNotifyPhone?: string;
  hasWhapiApiToken: boolean;
  whapiApiTokenLastChars?: string;
  whapiGroupId?: string;
};

export class UpdateOrganizationBaseUrlDto {
  baseUrl: string;
}

export class CreateOrganizationDto {
  name: string;
}

export class CreateAccountLinkDto {
  organizationId: string;

  returnUrl: string;

  refreshUrl: string;
}

// Org-level CGU edit. cguText: null clears the field (disables enforcement).
// Empty string is NOT accepted as a clear in the dashboard UX — Remove CGU is a
// distinct destructive action. cguEnforcement is the rollout-state toggle:
// 'strict' rejects on missing/stale acceptance, 'soft' records consent when
// supplied but never rejects (rollout window), 'off' is the kill-switch.
// Either field can be sent independently — sending only cguEnforcement does NOT
// touch cguText / cguVersion.
export enum OrganizationCguEnforcement {
  OFF = 'off',
  SOFT = 'soft',
  STRICT = 'strict',
}

export class UpdateOrganizationCguDto {
  cguText?: string | null;
  cguEnforcement?: OrganizationCguEnforcement;
}

// Both fields are independently nullable so admins can clear one back to the
// default while keeping the other. Cap at 500 chars to keep the modal readable;
// owners should write short, action-oriented copy, not policy walls.
export class UpdateOrganizationCancellationMessagesDto {
  cancellationMessageWithinDeadline?: string | null;
  cancellationMessageOutsideDeadline?: string | null;
}

// Per-tenant gate for the "Pay for others" flow. Operators toggle this via the
// dashboard; the value is read by mobile to decide whether to show the
// multi-player picker, and is also enforced server-side on the related
// endpoints. Mirrors UpdateOrganizationPayForOthersEnabledDto on the API.
export class UpdateOrganizationPayForOthersEnabledDto {
  // Optional: sub-setting saves omit it so they cannot overwrite a concurrent
  // toggle by another admin (the API leaves an absent flag untouched).
  payForOthersEnabled?: boolean;
  // Optional: the rolling-24h distinct-covered-players cap, 1..50. Omitted on
  // a plain toggle so the existing call shape keeps working.
  payForOthersDailyCap?: number;
  // Optional: guest players on the same flow, and their per-game ceiling 1..10.
  guestPlayersEnabled?: boolean;
  guestsPerGameMax?: number;
  // Optional: held seats (consent) on the same flow.
  payForOthersConsentRequired?: boolean;
}

// Phase 6 chat cutover flag. Mirrors the API's ChatBackend enum (defined on
// the Organization entity). Flipped via POST :id/chat-backend — elevated
// admins only.
export enum ChatBackend {
  STREAM = 'stream',
  INHOUSE = 'inhouse',
}

export class UpdateOrganizationChatBackendDto {
  chatBackend: ChatBackend;
}

// Body of the superadmin-only POST organizations/:id/recording-retention.
// null clears the override (back to the platform default).
export class UpdateOrganizationRecordingRetentionDto {
  days: number | null;
}

export class UpdateOrganizationSignupDocumentUploadDto {
  signupDocumentUploadEnabled: boolean;
  // Omitted = leave unchanged (the API only updates it when provided).
  signupDocumentUploadRequired?: boolean;
  signupDocumentUploadTitle?: string | null;
  signupDocumentUploadSubtitle?: string | null;
}

export class UpdateOrganizationDocumentVerificationDto {
  documentVerificationEnabled: boolean;
  documentVerificationMembershipTypeId?: string | null;
  documentVerificationCustomPrompt?: string | null;
}
